import { useTheme } from '@emotion/react';
import { useEffect, useRef, useState } from 'react';
import { shouldHighlightNavItem } from './navigation.utils';
import { mergeProps, useButton, useFocusRing } from 'react-aria';
import { useNavigationContext } from './navigation.context';
import { NavigationSubItem } from './navigation.types';

export const useItemIconColor = ({ isActive, shouldHighlight }: { isActive: boolean; shouldHighlight: boolean }) => {
  const theme = useTheme();
  const [isHovered, setIsHovered] = useState(false);

  let color = undefined;

  if (isActive && shouldHighlight) {
    color = theme.colors.primary.normal;
  }

  if (isHovered) {
    color = theme.colors.primary.normal;
  }

  return { color, setIsHovered };
};

// Define sidebar width constants
const SIDEBAR_CONFIG = {
  MIN_WIDTH: 88,
  COLLAPSED_THRESHOLD: 148,
  MAX_WIDTH: 368,
  INITIAL_WIDTH: 230,
};

// Extract resize logic into a custom hook
export const useResizableSidebar = (initialWidth = SIDEBAR_CONFIG.INITIAL_WIDTH) => {
  const [sidebarWidth, setSidebarWidth] = useState<number>(initialWidth);
  const sidebarRef = useRef<HTMLDivElement>(null);

  // Calculate if sidebar is in collapsed state
  const isNavigationCollapsed = sidebarWidth <= SIDEBAR_CONFIG.COLLAPSED_THRESHOLD;

  // Toggle sidebar between collapsed and expanded states
  const toggleSidebar = () => {
    if (isNavigationCollapsed) {
      // Expand the sidebar
      setSidebarWidth(SIDEBAR_CONFIG.INITIAL_WIDTH);
    } else {
      // Collapse the sidebar
      setSidebarWidth(SIDEBAR_CONFIG.MIN_WIDTH);
    }
  };

  return {
    sidebarRef,
    sidebarWidth,
    isNavigationCollapsed,
    toggleSidebar,
    setSidebarWidth,
  };
};

// Extract subitem handling logic
export const useNavItemState = ({
  id,
  subItems,
  onPress,
  isActive,
}: {
  id: string;
  subItems?: NavigationSubItem[];
  onPress?: () => void;
  isActive?: boolean;
}) => {
  const {
    expandedItems,
    clearExpandedItems,
    setActiveSubItem,
    toggleItemExpand,
    activeItem,
    setActiveMainItem,
    isNavigationCollapsed,
  } = useNavigationContext();

  const activeSubItemId = subItems?.find((subItem) => subItem.isActive)?.id;

  // Set active main item if isActive coming from parent props is true
  useEffect(() => {
    if (activeSubItemId) {
      setActiveSubItem(id, activeSubItemId);

      return;
    }

    if (isActive) {
      setActiveMainItem(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, id, activeSubItemId]);

  // Use context state for expansion
  const isExpanded = id ? expandedItems[id] : false;
  const isItemActive = id === activeItem.mainItemId;
  const isSubItemActive = subItems?.some((subItem) => subItem.id === activeItem.subItemId) ?? false;

  const hasSubItems = !!(subItems && subItems.length > 0);
  const showTooltip = !isExpanded && isNavigationCollapsed;

  const shouldHighlight = shouldHighlightNavItem({
    isNavigationCollapsed,
    isActive: isItemActive,
    isSubItemActive,
  });

  const handleItemPress = () => {
    if (isNavigationCollapsed) {
      clearExpandedItems();
    }

    if (hasSubItems && id) {
      toggleItemExpand(id);
    } else {
      if (id) {
        setActiveMainItem(id);
      }
      onPress?.();
    }
  };

  return {
    shouldHighlight,
    isNavigationCollapsed,
    isExpanded,
    isItemActive,
    hasSubItems,
    handleItemPress,
    showTooltip,
  };
};

// Extract navigation state management into a hook
export const useNavigationState = () => {
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});
  const [activeItem, setActiveItem] = useState<{ mainItemId: string | null; subItemId?: string }>({
    mainItemId: null,
    subItemId: undefined,
  });

  const { isNavigationCollapsed, ...resizableSidebar } = useResizableSidebar();

  // Clear expanded items when navigation is changing from expanded to collapsed
  useEffect(() => {
    if (isNavigationCollapsed) {
      clearExpandedItems();
    }
  }, [isNavigationCollapsed]);

  const clearExpandedItems = () => {
    setExpandedItems({});
  };

  const toggleItemExpand = (itemId: string) => {
    setExpandedItems((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  };

  const setItemExpanded = (itemId: string, isExpanded: boolean) => {
    setExpandedItems((prev) => ({
      ...prev,
      [itemId]: isExpanded,
    }));
  };

  const setActiveMainItem = (itemId: string | null) => {
    setActiveItem({
      mainItemId: itemId,
      subItemId: undefined,
    });
  };

  const setActiveSubItem = (mainItemId: string, subItemId: string) => {
    // Ensure parent is expanded when subitem is activated
    setExpandedItems((prev) => ({
      ...prev,
      [mainItemId]: true,
    }));

    setActiveItem({
      mainItemId,
      subItemId,
    });
  };

  return {
    expandedItems,
    activeItem,
    toggleItemExpand,
    clearExpandedItems,
    setItemExpanded,
    setActiveMainItem,
    setActiveSubItem,
    isNavigationCollapsed,
    ...resizableSidebar,
  };
};

// Create a hook for interactive element props
export const useInteractiveElement = (onPress?: () => void, elementType: 'button' | 'div' = 'div') => {
  const ref = useRef<HTMLButtonElement>(null);
  const { buttonProps } = useButton({ onPress, elementType }, ref);
  const { focusProps, isFocusVisible } = useFocusRing();

  return {
    ref,
    elementProps: mergeProps(buttonProps, focusProps),
    isFocusVisible,
  };
};
